import { HttpErrorResponse } from '@angular/common/http';

import { UrlAuthType } from '../models/provider.model';

/**
 * Helpers for feed files (`FeedFileService`), shared by the Files tab
 * (SIRI/generic, `FeedFileListComponent`) and the GTFS Static schedule tab.
 */

export interface FeedFileMetadataRow {
  key: string;
  value: string;
}

/** A value without a key, or a key used twice. Fully blank rows are ignored. */
export function hasInvalidMetadata(rows: FeedFileMetadataRow[]): boolean {
  const keys = new Set<string>();
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) {
      if (row.value.trim()) return true;
      continue;
    }
    if (keys.has(key)) return true;
    keys.add(key);
  }
  return false;
}

export function buildFileMetadata(rows: FeedFileMetadataRow[]): Record<string, string> {
  const metadata: Record<string, string> = {};
  for (const row of rows) {
    if (row.key.trim()) metadata[row.key.trim()] = row.value;
  }
  return metadata;
}

export function metadataRowsFrom(metadata: Record<string, string> | null | undefined): FeedFileMetadataRow[] {
  return Object.entries(metadata ?? {}).map(([key, value]) => ({ key, value }));
}

/**
 * Core's own message from an error response (`{ success: false, message }`,
 * e.g. "This feed already has a file named 'stops.csv'"), or null when the
 * body has none — then fall back to `statusErrorKey`. A 413 from the HTTP
 * layer (over 100 MB) typically has no body.
 */
export function apiErrorMessage(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const message = (err.error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message : null;
}

/** Fallback text for an error without a message: a key under
 *  `providers.feedFiles.errors`. */
export function statusErrorKey(status: number): 'tooLarge' | 'unavailable' | 'failed' {
  if (status === 413) return 'tooLarge';
  if (status === 503) return 'unavailable';
  return 'failed';
}

/** Check-interval presets offered for URL sources (ISO-8601; core's minimum is PT15M). */
export const URL_INTERVAL_PRESETS = ['PT15M', 'PT30M', 'PT1H', 'PT6H', 'PT24H'] as const;
/** Core's default when a URL source sets no interval. */
export const URL_DEFAULT_INTERVAL = 'PT15M';

export const URL_AUTH_TYPES: readonly UrlAuthType[] = ['NONE', 'BASIC', 'BEARER', 'HEADER', 'QUERY'];

/** Every auth type but NONE needs `secrets.URL.secret` while URL is the active
 *  source (a stored one counts). */
export function authNeedsSecret(type: UrlAuthType): boolean {
  return type !== 'NONE';
}

/** Core's filename rule: ≤255, a plain name — no path separators, not '.' or '..'. */
export function isPlainFilename(name: string): boolean {
  const n = name.trim();
  return !!n && n.length <= 255 && !/[\\/]/.test(n) && n !== '.' && n !== '..';
}

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Opens a blank tab synchronously — call inside the click handler — to point
 * at a download link once it has been fetched. Opening it only after the
 * async call would trip popup blockers. Links expire after 15 minutes, so
 * they're fetched on click rather than ahead of time.
 */
export function openDownloadTab(): { go(url: string): void; cancel(): void } {
  const win = window.open('', '_blank');
  return {
    go(url: string) {
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else {
        window.location.assign(url);
      }
    },
    cancel() {
      win?.close();
    },
  };
}
